// src/utils/DatatableFilterModel.js
// Pure utility — no React imports, no side-effects

export const TIME_FRAMES = [
  { key: 'last24Hours',  label: 'Last 24 Hours', granularity: 'hour'  },
  { key: 'currentMonth', label: 'Current Month', granularity: 'day'   },
  { key: 'sixMonths',    label: '6 Months',      granularity: 'month' },
  { key: 'oneYear',      label: '1 Year',        granularity: 'month' },
  { key: 'allTime',      label: 'All Time',      granularity: 'year'  },
];

export const PAGE_SIZES = [25, 50, 100, 200, 500];
export const DEFAULT_PAGE_SIZE = 100;

export const EMPTY_FILTERS = {
  runId:            '',
  runStartFrom:     '',
  runStartTo:       '',
  runEndFrom:       '',
  runEndTo:         '',
  sizeMin:          '',
  sizeMax:          '',
  elapsedMin:       '',
  elapsedMax:       '',
  sraRelFrom:       '',
  sraRelTo:         '',
  bt2HitsMin:       '',
  bt2HitsMax:       '',
  blastNHitsMin:    '',
  blastNHitsMax:    '',
  blastNRegionHits: false,
  blastNRegions:    [],
  hasBowtie2:       false,
  hasBlastNBt2:     false,
  hasBlastNSP:      false,
  hasSeqtk:         false,
  hasSPAdes:        false,
  // Metadata filters (arrays — multi-select)
  metaName:         [],
  metaOrganism:     [],
  metaPlatform:     [],
  metaCountry:      [],
  metaCenter:       [],
  metaContinent:    [],
  metaIsoSource:    [],
};

export const TOOL_CHECKBOXES = [
  { key: 'hasBowtie2',   label: 'bowtie2 — ReadHits data present'      },
  { key: 'hasBlastNBt2', label: 'blastN(bowtie2) — region hits present' },
  { key: 'hasBlastNSP',  label: 'blastN(SPAdes) — contig hits present'  },
  { key: 'hasSeqtk',     label: 'seqtk(bowtie2) — seqtk data present'  },
  { key: 'hasSPAdes',    label: 'SPAdes — assembly data present'        },
];

// ─────────────────────────────────────────────────────────────────────────────
//  VALUE HELPERS
// ─────────────────────────────────────────────────────────────────────────────

export function isEmpty(v) {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string')  return v.trim() === '';
  if (Array.isArray(v))       return v.length === 0;
  if (typeof v === 'object')  return Object.keys(v).length === 0;
  return false;
}

export function nested(row, path) {
  if (!row) return null;
  return path.split('.').reduce((acc, k) => acc?.[k], row);
}

export function hasToolData(v) {
  return Boolean(v) && typeof v === 'object' && Object.keys(v).length > 0;
}

export function fmtDate(v) {
  if (!v) return 'N/A';
  return new Date(v).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

// ─────────────────────────────────────────────────────────────────────────────
//  API PARAM BUILDERS
// ─────────────────────────────────────────────────────────────────────────────

export function buildDateParams(timeFrameKey) {
  const p   = new URLSearchParams();
  const now = new Date();

  switch (timeFrameKey) {
    case 'last24Hours': {
      const d = new Date(now); d.setHours(d.getHours() - 24);
      p.set('startDate', d.toISOString()); p.set('endDate', now.toISOString()); break;
    }
    case 'currentMonth': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      p.set('startDate', start.toISOString()); p.set('endDate', now.toISOString()); break;
    }
    case 'sixMonths': {
      const d = new Date(now); d.setMonth(d.getMonth() - 6);
      p.set('startDate', d.toISOString()); p.set('endDate', now.toISOString()); break;
    }
    case 'oneYear': {
      const d = new Date(now); d.setFullYear(d.getFullYear() - 1);
      p.set('startDate', d.toISOString()); p.set('endDate', now.toISOString()); break;
    }
    case 'allTime': default: break;
  }
  return p;
}

// ─────────────────────────────────────────────────────────────────────────────
//  buildQueryParams
//
//  cachedTotal: the client's last known totalCount, sent on page 2+ so the
//  server can skip countDocuments(). On page 0 (first load or any
//  filter/sort/timeframe change) totalCount is 0 so cachedTotal is not sent
//  and the server always counts fresh.
// ─────────────────────────────────────────────────────────────────────────────
export function buildQueryParams({
  timeFrameKey, filters, readHitsOnly,
  page, pageSize, sortKey, sortDir,
  cachedTotal,
}) {
  const p = buildDateParams(timeFrameKey);

  p.set('page',                 String(page     ?? 0));
  p.set('pageSize',             String(pageSize ?? DEFAULT_PAGE_SIZE));
  p.set('sortKey',              sortKey ?? 'RunStartDate');
  p.set('sortDir',              sortDir ?? 'desc');
  p.set('includePaginationMeta', 'true');

  // Send cached total on page turns so the server can skip countDocuments()
  if (page > 0 && cachedTotal > 0) {
    p.set('cachedTotal', String(cachedTotal));
  }

  if (readHitsOnly) p.set('readHitsOnly', 'true');

  if (filters.runId?.trim()) p.set('runId', filters.runId.trim());

  if (filters.runStartFrom) p.set('startDate', new Date(filters.runStartFrom).toISOString());
  if (filters.runStartTo)   p.set('endDate',   new Date(filters.runStartTo).toISOString());

  if (filters.sizeMin !== '') p.set('sizeMin', filters.sizeMin);
  if (filters.sizeMax !== '') p.set('sizeMax', filters.sizeMax);

  if (filters.elapsedMin !== '') p.set('elapsedMin', filters.elapsedMin);
  if (filters.elapsedMax !== '') p.set('elapsedMax', filters.elapsedMax);

  if (filters.sraRelFrom) p.set('sraRelFrom', new Date(filters.sraRelFrom).toISOString());
  if (filters.sraRelTo)   p.set('sraRelTo',   new Date(filters.sraRelTo).toISOString());

  if (filters.bt2HitsMin !== '') p.set('bt2HitsMin', filters.bt2HitsMin);
  if (filters.bt2HitsMax !== '') p.set('bt2HitsMax', filters.bt2HitsMax);

  if (filters.blastNHitsMin !== '') p.set('blastNHitsMin', filters.blastNHitsMin);
  if (filters.blastNHitsMax !== '') p.set('blastNHitsMax', filters.blastNHitsMax);

  if (filters.blastNRegionHits) p.set('blastNRegionHits', 'true');
  if (filters.blastNRegions?.length > 0) p.set('blastNRegions', filters.blastNRegions.join(','));

  if (!readHitsOnly) {
    if (filters.hasBowtie2)   p.set('hasBowtie2',   'true');
    if (filters.hasBlastNBt2) p.set('hasBlastNBt2', 'true');
    if (filters.hasBlastNSP)  p.set('hasBlastNSP',  'true');
    if (filters.hasSeqtk)     p.set('hasSeqtk',     'true');
    if (filters.hasSPAdes)    p.set('hasSPAdes',    'true');
  }

  // Use repeated params instead of comma-joining so values containing commas
  // (e.g. "University of California, San Diego") are not split incorrectly.
  (filters.metaName      || []).forEach(v => p.append('metaName',      v));
  (filters.metaOrganism  || []).forEach(v => p.append('metaOrganism',  v));
  (filters.metaPlatform  || []).forEach(v => p.append('metaPlatform',  v));
  (filters.metaCountry   || []).forEach(v => p.append('metaCountry',   v));
  (filters.metaCenter    || []).forEach(v => p.append('metaCenter',    v));
  (filters.metaContinent || []).forEach(v => p.append('metaContinent', v));
  (filters.metaIsoSource || []).forEach(v => p.append('metaIsoSource', v));

  return p;
}

export function buildAggregateParams(timeFrameKey, granularity, readHitsOnly, filters = EMPTY_FILTERS) {
  const p = buildDateParams(timeFrameKey);
  p.set('granularity', granularity);
  if (readHitsOnly) p.set('readHitsOnly', 'true');
  if (filters.runId?.trim()) p.set('runId', filters.runId.trim());
  if (filters.sizeMin !== '') p.set('sizeMin', filters.sizeMin);
  if (filters.sizeMax !== '') p.set('sizeMax', filters.sizeMax);
  if (!readHitsOnly) {
    if (filters.hasBowtie2)   p.set('hasBowtie2',   'true');
    if (filters.hasBlastNBt2) p.set('hasBlastNBt2', 'true');
    if (filters.hasBlastNSP)  p.set('hasBlastNSP',  'true');
    if (filters.hasSeqtk)     p.set('hasSeqtk',     'true');
    if (filters.hasSPAdes)    p.set('hasSPAdes',    'true');
  }
  return p;
}

// ─────────────────────────────────────────────────────────────────────────────
//  CHART HELPERS
// ─────────────────────────────────────────────────────────────────────────────

export function formatBucketLabel(bucket, granularity) {
  if (!bucket) return '';
  try {
    switch (granularity) {
      case 'hour': {
        const [datePart, hourPart] = bucket.split('T');
        const [y, m, d] = datePart.split('-').map(Number);
        return new Date(y, m - 1, d, Number(hourPart)).toLocaleString('en-US', {
          month: 'short', day: 'numeric', hour: 'numeric', hour12: true,
        });
      }
      case 'day': {
        const [y, m, d] = bucket.split('-').map(Number);
        return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
      case 'month': {
        const [y, m] = bucket.split('-').map(Number);
        return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      }
      case 'year': return bucket;
      default:     return bucket;
    }
  } catch { return bucket; }
}

export function fillGaps(backendBuckets, granularity) {
  if (!backendBuckets || backendBuckets.length === 0) return [];
  const lookup = new Map(backendBuckets.map((b) => [b.bucket, b.count]));
  const first  = parseBucket(backendBuckets[0].bucket, granularity);
  const last   = parseBucket(backendBuckets[backendBuckets.length - 1].bucket, granularity);
  if (!first || !last) return backendBuckets;
  const result = [];
  const cursor = new Date(first);
  while (cursor <= last) {
    const key = formatBucketKey(cursor, granularity);
    result.push({ bucket: key, label: formatBucketLabel(key, granularity), count: lookup.get(key) ?? 0 });
    advanceCursor(cursor, granularity);
  }
  return result;
}

function parseBucket(bucket, granularity) {
  try {
    switch (granularity) {
      case 'hour':  { const [dp, hp] = bucket.split('T'); const [y,m,d] = dp.split('-').map(Number); return new Date(y,m-1,d,Number(hp)); }
      case 'day':   { const [y,m,d] = bucket.split('-').map(Number); return new Date(y,m-1,d); }
      case 'month': { const [y,m] = bucket.split('-').map(Number); return new Date(y,m-1,1); }
      case 'year':  return new Date(Number(bucket), 0, 1);
      default: return null;
    }
  } catch { return null; }
}

function formatBucketKey(date, granularity) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  switch (granularity) {
    case 'hour':  return `${y}-${m}-${d}T${h}`;
    case 'day':   return `${y}-${m}-${d}`;
    case 'month': return `${y}-${m}`;
    case 'year':  return String(y);
    default:      return String(y);
  }
}

function advanceCursor(date, granularity) {
  switch (granularity) {
    case 'hour':  date.setHours(date.getHours() + 1);       break;
    case 'day':   date.setDate(date.getDate() + 1);         break;
    case 'month': date.setMonth(date.getMonth() + 1);       break;
    case 'year':  date.setFullYear(date.getFullYear() + 1); break;
  }
}

export function granularityLabel(g) {
  return { hour: 'Hour-by-Hour', day: 'Day-by-Day', month: 'Month-by-Month', year: 'Year-by-Year' }[g] ?? g;
}

// ─────────────────────────────────────────────────────────────────────────────
//  FILTER TAG HELPERS
// ─────────────────────────────────────────────────────────────────────────────

export function countActiveFilters(f) {
  return [
    f.runId,
    f.runStartFrom,
    f.runStartTo,
    f.runEndFrom,
    f.runEndTo,
    f.sizeMin !== '',
    f.sizeMax !== '',
    f.elapsedMin !== '',
    f.elapsedMax !== '',
    f.sraRelFrom,
    f.sraRelTo,
    f.bt2HitsMin !== '',
    f.bt2HitsMax !== '',
    f.blastNHitsMin !== '',
    f.blastNHitsMax !== '',
    f.blastNRegions?.length > 0,
    f.hasBowtie2,
    f.hasBlastNBt2,
    f.hasBlastNSP,
    f.hasSeqtk,
    f.hasSPAdes,
    f.metaName?.length > 0,
    f.metaOrganism?.length > 0,
    f.metaPlatform?.length > 0,
    f.metaCountry?.length > 0,
    f.metaCenter?.length > 0,
    f.metaContinent?.length > 0,
    f.metaIsoSource?.length > 0,
  ].filter(Boolean).length;
}

export function buildFilterTags(filters, onClear) {
  return [
    filters.runId              && { label: `Run ∋ "${filters.runId}"`,              clear: () => onClear({ runId: '' }) },
    filters.runStartFrom       && { label: `Start ≥ ${filters.runStartFrom}`,       clear: () => onClear({ runStartFrom: '' }) },
    filters.runStartTo         && { label: `Start ≤ ${filters.runStartTo}`,         clear: () => onClear({ runStartTo: '' }) },
    filters.runEndFrom         && { label: `End ≥ ${filters.runEndFrom}`,           clear: () => onClear({ runEndFrom: '' }) },
    filters.runEndTo           && { label: `End ≤ ${filters.runEndTo}`,             clear: () => onClear({ runEndTo: '' }) },
    filters.sizeMin !== ''     && { label: `Size ≥ ${filters.sizeMin}`,             clear: () => onClear({ sizeMin: '' }) },
    filters.sizeMax !== ''     && { label: `Size ≤ ${filters.sizeMax}`,             clear: () => onClear({ sizeMax: '' }) },
    filters.elapsedMin !== ''  && { label: `Elapsed ≥ ${filters.elapsedMin}s`,      clear: () => onClear({ elapsedMin: '' }) },
    filters.elapsedMax !== ''  && { label: `Elapsed ≤ ${filters.elapsedMax}s`,      clear: () => onClear({ elapsedMax: '' }) },
    filters.sraRelFrom         && { label: `SRA Rel ≥ ${filters.sraRelFrom}`,       clear: () => onClear({ sraRelFrom: '' }) },
    filters.sraRelTo           && { label: `SRA Rel ≤ ${filters.sraRelTo}`,         clear: () => onClear({ sraRelTo: '' }) },
    filters.bt2HitsMin !== ''  && { label: `bt2 Hits ≥ ${filters.bt2HitsMin}`,      clear: () => onClear({ bt2HitsMin: '' }) },
    filters.bt2HitsMax !== ''  && { label: `bt2 Hits ≤ ${filters.bt2HitsMax}`,      clear: () => onClear({ bt2HitsMax: '' }) },
    filters.blastNHitsMin !== ''&& { label: `blastN Hits ≥ ${filters.blastNHitsMin}`,clear: () => onClear({ blastNHitsMin: '' }) },
    filters.blastNHitsMax !== ''&& { label: `blastN Hits ≤ ${filters.blastNHitsMax}`,clear: () => onClear({ blastNHitsMax: '' }) },
    filters.blastNRegionHits   && { label: 'blastN RegionHits = true',              clear: () => onClear({ blastNRegionHits: false }) },
    filters.blastNRegions?.length > 0 && { label: `Regions: ${filters.blastNRegions.join(', ')}`, clear: () => onClear({ blastNRegions: [] }) },
    filters.hasBowtie2         && { label: 'has bowtie2',                           clear: () => onClear({ hasBowtie2: false }) },
    filters.hasBlastNBt2       && { label: 'has blastN(bowtie2)',                   clear: () => onClear({ hasBlastNBt2: false }) },
    filters.hasBlastNSP        && { label: 'has blastN(SPAdes)',                    clear: () => onClear({ hasBlastNSP: false }) },
    filters.hasSeqtk           && { label: 'has seqtk',                             clear: () => onClear({ hasSeqtk: false }) },
    filters.hasSPAdes          && { label: 'has SPAdes',                            clear: () => onClear({ hasSPAdes: false }) },
    filters.metaName?.length > 0      && { label: `Name: ${filters.metaName.join(', ')}`,           clear: () => onClear({ metaName: [] }) },
    filters.metaOrganism?.length > 0  && { label: `Organism: ${filters.metaOrganism.join(', ')}`,  clear: () => onClear({ metaOrganism: [] }) },
    filters.metaPlatform?.length > 0  && { label: `Platform: ${filters.metaPlatform.join(', ')}`,  clear: () => onClear({ metaPlatform: [] }) },
    filters.metaCountry?.length > 0   && { label: `Country: ${filters.metaCountry.join(', ')}`,    clear: () => onClear({ metaCountry: [] }) },
    filters.metaCenter?.length > 0    && { label: `Center: ${filters.metaCenter.join(', ')}`,      clear: () => onClear({ metaCenter: [] }) },
    filters.metaContinent?.length > 0 && { label: `Continent: ${filters.metaContinent.join(', ')}`,clear: () => onClear({ metaContinent: [] }) },
    filters.metaIsoSource?.length > 0 && { label: `Iso Source: ${filters.metaIsoSource.join(', ')}`,clear: () => onClear({ metaIsoSource: [] }) },
  ].filter(Boolean);
}

// ─────────────────────────────────────────────────────────────────────────────
//  PAGINATION HELPERS
// ─────────────────────────────────────────────────────────────────────────────

export function buildPageButtons(totalPages, currentPage, windowSize = 2) {
  const btns = [];
  for (let i = 0; i < totalPages; i++) {
    if (i === 0 || i === totalPages - 1 || Math.abs(i - currentPage) <= windowSize) {
      btns.push(i);
    } else if (btns[btns.length - 1] !== '…') {
      btns.push('…');
    }
  }
  return btns;
}

// ─────────────────────────────────────────────────────────────────────────────
//  COLUMN DEFINITIONS
// ─────────────────────────────────────────────────────────────────────────────

export const COLUMN_DEFS = [
  { key: 'Run',            label: 'Run ID',              sortable: true,  serverKey: 'Run',           type: 'link'       },
  { key: 'ReadHits_bt2',   label: 'ReadHits (bt2)',       sortable: false, type: 'mono',       optional: true, accessor: (r) => nested(r, 'bowtie2.PolioAnno.ReadHits') },
  { key: 'HitsCount_bt2',  label: 'Hits# (bt2)',          sortable: false, type: 'mono',       optional: true, accessor: (r) => nested(r, 'bowtie2.PolioAnno.ReadHitsCount') },
  { key: 'blastN(SPAdes)', label: 'blastN(SPAdes)',       sortable: false, type: 'toolButton', optional: true, endpoint: 'ContigHits', accessor: (r) => r['blastN(SPAdes)'] },
  { key: 'ReadHits_seqtk', label: 'ReadHits (seqtk)',     sortable: false, type: 'mono',       optional: true, accessor: (r) => nested(r, 'seqtk(bowtie2).PolioAnno.ReadHits') },
  { key: 'HitsCount_seqtk',label: 'Hits# (seqtk)',        sortable: false, type: 'mono',       optional: true, accessor: (r) => nested(r, 'seqtk(bowtie2).PolioAnno.ReadHitsCount') },
  { key: 'blastN(bowtie2)',label: 'blastN(bt2)',          sortable: false, type: 'toolButton', optional: true, endpoint: 'ReadHits',   accessor: (r) => r['blastN(bowtie2)'] },
  { key: 'RegionHits',     label: 'RegionHits (blastN)',  sortable: false, type: 'mono',       optional: true, accessor: (r) => nested(r, 'blastN(bowtie2).PolioAnno.RegionHits') },
  { key: 'RegionHitsCount',label: 'RegionHits# (blastN)', sortable: false, type: 'mono',       optional: true, accessor: (r) => nested(r, 'blastN(bowtie2).PolioAnno.RegionHitsCount') },
  { key: 'Size',           label: 'Size',                 sortable: true,  serverKey: 'Size',           type: 'mono'      },
  { key: 'SRAReleaseDate', label: 'SRA Release',          sortable: true,  serverKey: 'SRAReleaseDate', type: 'date'      },
  { key: 'RunElapsedTime', label: 'Elapsed',              sortable: true,  serverKey: 'RunElapsedTime', type: 'mono'      },
  { key: 'RunStartDate',   label: 'Start Date',           sortable: true,  serverKey: 'RunStartDate',   type: 'date'      },
  { key: 'RunEndDate',     label: 'End Date',             sortable: true,  serverKey: 'RunEndDate',     type: 'date'      },
  // Metadata columns — optional, only shown when data is present
  { key: 'meta_name',             label: 'Name',             sortable: false, type: 'mono', accessor: (r) => r?.Metadata?.name },
  { key: 'meta_organism',         label: 'Organism',         sortable: false, type: 'mono', accessor: (r) => r?.Metadata?.organism },
  { key: 'meta_platform',         label: 'Platform',         sortable: false, type: 'mono', accessor: (r) => r?.Metadata?.platform },
  { key: 'meta_country',          label: 'Country',          sortable: false, type: 'mono', accessor: (r) => r?.Metadata?.geo_loc_name_country_calc },
  { key: 'meta_continent',        label: 'Continent',        sortable: false, type: 'mono', accessor: (r) => r?.Metadata?.geo_loc_name_country_continent_calc },
  { key: 'meta_center',           label: 'Center',           sortable: false, type: 'mono', optional: true, accessor: (r) => r?.Metadata?.center_name },
  { key: 'meta_isolation_source', label: 'Isolation Source', sortable: false, type: 'mono', accessor: (r) => r?.Metadata?.isolation_source_sam },
  { key: 'meta_collection_date',  label: 'Collection Date',  sortable: false, type: 'mono', optional: true, accessor: (r) => r?.Metadata?.Attributes?.collection_date_sam ?? r?.Metadata?.collection_date_sam },
  { key: '_preview',       label: 'Preview',              sortable: false, type: 'previewButton'                          },
];